import React from 'react';
import { cn } from '@/utils/common/helpers';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | number;
type IconWeight = 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | number;
type IconStyle = 'outlined' | 'filled';
type IconContext = 'primary' | 'secondary' | 'tertiary' | 'critical';
type IconGrade = number; // -50 to 200

interface GoogleMaterialIconProps {
  /**
   * The Material Symbol name (e.g., 'calendar_today', 'person', 'settings')
   * Find icons at: https://fonts.google.com/icons
   */
  name: string;
  
  /**
   * Icon size - predefined sizes or custom number (in px)
   */
  size?: IconSize;
  
  /**
   * Icon weight - affects thickness of strokes
   */
  weight?: IconWeight;
  
  /**
   * Icon style - outlined (stroke) or filled (solid)
   */
  iconStyle?: IconStyle;
  
  /**
   * Icon grade - fine-tune visual weight (-50 to 200)
   */
  grade?: IconGrade;
  
  /**
   * Context-based styling for consistent design system usage
   */
  context?: IconContext;
  
  /**
   * Enable interactive hover effects
   */
  interactive?: boolean;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles (use sparingly - prefer CSS custom properties)
   */
  style?: React.CSSProperties;
  
  /**
   * Accessibility label
   */
  'aria-label'?: string;
  
  /**
   * Click handler for interactive icons
   */
  onClick?: () => void;
}

/**
 * GoogleMaterialIcon - A React component for Google Material Symbols
 * 
 * Features:
 * - Variable font support (weight, grade, optical size, fill)
 * - Theme-aware (automatically adjusts for light/dark mode)
 * - Size and context variants
 * - Interactive hover effects
 * - Full customization via CSS custom properties
 * 
 * @example
 * // Basic usage
 * <GoogleMaterialIcon name="calendar_today" />
 * 
 * // With customization
 * <GoogleMaterialIcon 
 *   name="person" 
 *    
 *   weight="medium"
 *   iconStyle="filled"
 *   context="primary"
 *   interactive 
 * />
 * 
 * // Custom styling
 * <GoogleMaterialIcon 
 *   name="settings"
 *   className="text-blue-500"
 *   style={{ '--icon-grade': 50 }}
 * />
 */
const GoogleMaterialIcon: React.FC<GoogleMaterialIconProps> = ({
  name,
  size = 'md',
  weight,
  iconStyle = 'outlined',
  grade,
  context,
  interactive = false,
  className = '',
  style,
  'aria-label': ariaLabel,
  onClick,
}) => {
  // Convert size to CSS class or custom property
  const getSizeClass = () => {
    if (typeof size === 'number') {
      return ''; // Will be handled by custom CSS property
    }
    return `icon-${size}`;
  };

  // Convert weight to CSS custom property value
  const getWeightValue = (): number => {
    if (typeof weight === 'number') return weight;
    
    switch (weight) {
      case 'light': return 300;
      case 'regular': return 400;
      case 'medium': return 500;
      case 'semibold': return 600;
      case 'bold': return 700;
      default: return 400; // Let CSS custom property handle default
    }
  };

  // Build CSS custom properties for inline overrides
  const customProperties: React.CSSProperties = {
    ...style,
  };

  // Apply size override if numeric
  if (typeof size === 'number') {
    customProperties['--icon-optical-size'] = size >= 32 ? 40 : size >= 24 ? 24 : 20;
    customProperties.fontSize = `${size}px`;
  }

  // Apply weight override
  if (weight !== undefined) {
    customProperties['--icon-weight'] = getWeightValue();
  }

  // Apply grade override
  if (grade !== undefined) {
    customProperties['--icon-grade'] = grade;
  }

  // Apply style override
  if (iconStyle === 'filled') {
    customProperties['--icon-fill'] = 1;
  } else {
    customProperties['--icon-fill'] = 0;
  }

  // Build CSS classes
  const classes = cn(
    'material-symbols-outlined',
    getSizeClass(),
    iconStyle === 'filled' ? 'icon-filled' : 'icon-outlined',
    context && `icon-${context}`,
    interactive && 'icon-interactive',
    onClick && 'cursor-pointer',
    className
  );

  return (
    <span
      className={classes}
      style={{
        ...customProperties,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: '1',
        verticalAlign: 'middle'
      }}
      aria-label={ariaLabel || name.replace(/_/g, ' ')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {name}
    </span>
  );
};

export default GoogleMaterialIcon;

// Type exports for external use
export type {
  IconSize,
  IconWeight,
  IconStyle,
  IconContext,
  IconGrade,
  GoogleMaterialIconProps,
};